/**
 * Service for searching and retrieving creator-related content.
 *
 * Responsibilities:
 * - Search creators with filters and sorting.
 * - Fetch verified creators list.
 * - Search creator previews (gif list) with the same filters.
 */
import { IGifList, IShortUserList, IUserSearchResults } from "../types";
import type { ApiClient } from "@/common/api/http/types";

export default class CreatorsService {
  public constructor(private readonly client: ApiClient) {}

  async searchCreators(params: {
    query?: string;
    order?: string;
    page?: number;
    count?: number;
    tags?: string;
    verified?: boolean;
  }): Promise<IUserSearchResults> {
    const query = new URLSearchParams();

    query.append("order", params.order ?? "trending");
    query.append("page", (params.page ?? 1).toString());

    if (params.count) {
      query.append("count", params.count.toString());
    }

    if (params.query) {
      query.append("query", params.query);
    }

    if (params.verified) {
      query.append("verified", "y");
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    return await this.client.get(`v2/creators/search?${query}`);
  }

  async getNewVerifiedCreators(): Promise<IShortUserList> {
    return await this.client.get("v2/creators/verified");
  }

  async searchCreatorsPreviews(params: {
    query?: string;
    order?: string;
    page?: number;
    count?: number;
    tags?: string;
    verified?: boolean;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    query.append("order", params.order ?? "trending");
    query.append("page", (params.page ?? 1).toString());

    if (params.count) {
      query.append("count", params.count.toString());
    }

    if (params.query) {
      query.append("query", params.query);
    }

    if (params.verified) {
      query.append("verified", "y");
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    return await this.client.get(`v2/creators/search/previews?${query}`);
  }
}
